/* eslint-disable react/prop-types */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PreviewIcon from "@mui/icons-material/Preview";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import {
  getEmailToolEmail,
  getEmailToolRecipients,
  getEmailToolSettings,
  saveEmailToolEmail,
} from "apis/controllers/emailTool";
import { CssTextField } from "qubCommon/Inputs/CssTextField";
import ActionConfirmDialog from "./components/ActionConfirmDialog";
import MailBodyEditor from "./components/MailBodyEditor";
import MailDeliveryLog from "./components/MailDeliveryLog";
import MailStatusChip from "./components/MailStatusChip";
import RecipientFilters from "./components/RecipientFilters";
import RecipientsDialog from "./components/RecipientsDialog";
import TestEmailDialog from "./components/TestEmailDialog";
import {
  DEFAULT_RECIPIENT_FILTERS,
  EMAIL_CONFIRMED_OPTIONS,
  EMAIL_ACTIONS,
  EMAIL_MESSAGE_STATUSES,
  DELIVERY_LOG_MESSAGE_STATUSES,
  DELIVERY_LOG_POLLING_MESSAGE_STATUSES,
  MAIL_TOOL_STOP_WARNING_BUTTON_SX,
} from "./mailToolConstants";
import {
  formatDateTime,
  getOptionLabel,
  getOptionValue,
  getRecipientKey,
  getResponseMessage,
  getSelectedRecipientUserIds,
  getValue,
  isEditableStatus,
  isErrorResponse,
  isSendableStatus,
  isStoppableStatus,
  normalizeEmailDetail,
  normalizeFiltersForApi,
  normalizeRecipientCandidate,
  readResponseData,
} from "./mailToolUtils";

const formSchema = yup.object({
  subject: yup
    .string()
    .trim()
    .required("Το θέμα είναι υποχρεωτικό")
    .max(300, "Το θέμα δεν μπορεί να ξεπερνά τους 300 χαρακτήρες"),
  body: yup.string().trim().required("Το κείμενο είναι υποχρεωτικό"),
});

const emptyFormValues = {
  subject: "",
  body: "",
};

const getConfirmConfig = (action) => {
  if (action === EMAIL_ACTIONS.DELETE) {
    return {
      title: "Διαγραφή μηνύματος",
      description: "Το μήνυμα θα αφαιρεθεί από τη λίστα του εργαλείου.",
      confirmLabel: "Διαγραφή",
      confirmColor: "error",
    };
  }

  return {
    title: "Διακοπή αποστολής",
    description:
      "Οι παραλήπτες που βρίσκονται σε αναμονή ή επεξεργασία θα σημειωθούν ως σταματημένοι.",
    confirmLabel: "Διακοπή",
    confirmColor: "warning",
    confirmSx: MAIL_TOOL_STOP_WARNING_BUTTON_SX,
  };
};

const getLookupSummary = (options, value) => {
  if (value === "" || value === null || value === undefined) return "";

  const match = options.find(
    (option) => String(getOptionValue(option)) === String(value),
  );
  return match ? getOptionLabel(match) : String(value);
};

const getLookupSummaryList = (options, values) => {
  const selectedValues = Array.isArray(values) ? values : [];
  if (selectedValues.length === 0) return "";

  const labels = selectedValues.map((value) =>
    getLookupSummary(options, value),
  );

  return labels.length <= 2 ? labels.join(", ") : `${labels.length} επιλεγμένα`;
};

const MailToolEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: emptyFormValues,
    resolver: yupResolver(formSchema),
    mode: "onBlur",
  });

  const [mail, setMail] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_RECIPIENT_FILTERS);
  const [recipients, setRecipients] = useState([]);
  const [dialogRecipients, setDialogRecipients] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [applicationStatuses, setApplicationStatuses] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(!isNew);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [testEmailMessage, setTestEmailMessage] = useState(null);
  const [bodyTab, setBodyTab] = useState("visual");
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const bodyValue = watch("body") || "";
  const statusId = mail?.statusId;
  const readOnly = !isEditableStatus(statusId, isNew);
  const canSend = isSendableStatus(statusId, isNew);
  const canStop = !isNew && isStoppableStatus(statusId);
  const canDelete = !isNew;
  const showRecipientStatusColumn = [
    EMAIL_MESSAGE_STATUSES.SENT,
    EMAIL_MESSAGE_STATUSES.PENDING,
    EMAIL_MESSAGE_STATUSES.PROCESSING,
    EMAIL_MESSAGE_STATUSES.PARTIALLY_SENT,
  ].includes(statusId);
  const showDeliveryLog =
    !isNew &&
    Boolean(mail?.id || id) &&
    DELIVERY_LOG_MESSAGE_STATUSES.includes(statusId);
  const pollDeliveryLog =
    showDeliveryLog && DELIVERY_LOG_POLLING_MESSAGE_STATUSES.includes(statusId);
  const selectedRecipientIds = useMemo(
    () => getSelectedRecipientUserIds(recipients),
    [recipients],
  );
  const recipientFilterSummary = useMemo(() => {
    const summary = [];
    const addSummary = (label, value) => {
      if (value) summary.push(`${label}: ${value}`);
    };

    addSummary("Περίοδος", getLookupSummaryList(seasons, filters.seasonIds));
    addSummary(
      "Τύπος",
      getLookupSummaryList(applicationTypes, filters.applicationTypeIds),
    );
    addSummary(
      "Κατάσταση",
      getLookupSummaryList(applicationStatuses, filters.applicationStatusIds),
    );
    addSummary("Φύλο", getLookupSummary(genders, filters.genderId));
    addSummary(
      "Email",
      getLookupSummary(EMAIL_CONFIRMED_OPTIONS, filters.emailConfirmed),
    );
    addSummary("Αναζήτηση", filters.search?.trim());

    return summary.length > 0 ? summary : ["Όλα τα φίλτρα"];
  }, [applicationStatuses, applicationTypes, filters, genders, seasons]);

  const backendAllowsAction = useCallback(
    (action) =>
      availableActions.length === 0 || availableActions.includes(action),
    [availableActions],
  );

  const loadLookups = useCallback(async () => {
    setLookupLoading(true);
    try {
      const settingsResponse = await getEmailToolSettings();

      if (isErrorResponse(settingsResponse)) {
        enqueueSnackbar(getResponseMessage(settingsResponse), {
          variant: "error",
        });
      } else {
        const settings = readResponseData(settingsResponse, {});
        const lookups = getValue(settings, "lookups", "Lookups") || {};
        setAvailableActions(getValue(settings, "actions", "Actions") || []);
        setSeasons(getValue(lookups, "seasons", "Seasons") || []);
        setApplicationTypes(
          getValue(lookups, "applicationTypes", "ApplicationTypes") || [],
        );
        setGenders(getValue(lookups, "genders", "Genders") || []);
        setApplicationStatuses(
          getValue(lookups, "applicationStatuses", "ApplicationStatuses") || [],
        );
      }
    } catch (error) {
      enqueueSnackbar(
        getResponseMessage(error, "Δεν ήταν δυνατή η φόρτωση των επιλογών"),
        {
          variant: "error",
        },
      );
    } finally {
      setLookupLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadEmail = useCallback(
    async (showLoader = true) => {
      if (isNew) return;
      if (showLoader) setDetailLoading(true);

      try {
        const response = await getEmailToolEmail(id);
        if (isErrorResponse(response)) {
          enqueueSnackbar(getResponseMessage(response), { variant: "error" });
          return;
        }

        const detail = normalizeEmailDetail(readResponseData(response));
        setMail(detail);
        reset({
          subject: detail.subject,
          body: detail.body,
        });
        setRecipients(detail.recipients);
        setDialogRecipients(detail.recipients);
        setFilters(detail.filters || DEFAULT_RECIPIENT_FILTERS);
        setFiltersExpanded(false);
      } catch (error) {
        enqueueSnackbar(
          getResponseMessage(error, "Δεν ήταν δυνατή η φόρτωση του μηνύματος"),
          {
            variant: "error",
          },
        );
      } finally {
        if (showLoader) setDetailLoading(false);
      }
    },
    [enqueueSnackbar, id, isNew, reset],
  );

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (isNew) {
      setDetailLoading(false);
      setMail(null);
      setRecipients([]);
      setDialogRecipients([]);
      setFilters(DEFAULT_RECIPIENT_FILTERS);
      setFiltersExpanded(true);
      reset(emptyFormValues);
      return;
    }

    loadEmail();
  }, [isNew, loadEmail, reset]);

  const handleFindRecipients = async () => {
    setRecipientLoading(true);
    try {
      const response = await getEmailToolRecipients(
        normalizeFiltersForApi(filters),
      );
      if (isErrorResponse(response)) {
        enqueueSnackbar(getResponseMessage(response), { variant: "error" });
        return;
      }

      const data = readResponseData(response, []);
      const previousSelection = new Map(
        recipients.map((recipient) => [
          getRecipientKey(recipient),
          recipient.selected,
        ]),
      );
      const normalized = Array.isArray(data)
        ? data.map((recipient) => {
            const next = normalizeRecipientCandidate(recipient);
            return {
              ...next,
              selected: previousSelection.get(getRecipientKey(next)) ?? true,
            };
          })
        : [];

      setDialogRecipients(normalized);
      setRecipientsOpen(normalized.length > 0);
      if (normalized.length > 0) {
        setFiltersExpanded(false);
      }
      enqueueSnackbar(
        normalized.length > 0
          ? `${normalized.length} παραλήπτες φορτώθηκαν`
          : "Δεν βρέθηκαν παραλήπτες",
        { variant: normalized.length > 0 ? "success" : "info" },
      );
    } catch (error) {
      enqueueSnackbar(
        getResponseMessage(error, "Δεν ήταν δυνατή η φόρτωση των παραληπτών"),
        {
          variant: "error",
        },
      );
    } finally {
      setRecipientLoading(false);
    }
  };

  const buildPayload = (action, values) => {
    const explicitRecipientIds = getSelectedRecipientUserIds(recipients);
    return {
      id: isNew ? null : mail?.id || id,
      action,
      subject: values.subject.trim(),
      body: values.body,
      filters: normalizeFiltersForApi(filters, false),
      recipients: explicitRecipientIds,
    };
  };

  const runAction = async (action, values) => {
    if (!backendAllowsAction(action)) {
      enqueueSnackbar(
        "Η ενέργεια δεν είναι διαθέσιμη από τις ρυθμίσεις του διακομιστή",
        {
          variant: "error",
        },
      );
      return;
    }

    const payload = buildPayload(action, values);
    if (action === EMAIL_ACTIONS.SEND && payload.recipients.length === 0) {
      enqueueSnackbar("Επιλέξτε τουλάχιστον έναν παραλήπτη πριν την αποστολή", {
        variant: "error",
      });
      return;
    }

    setActionLoading(action);
    try {
      const response = await saveEmailToolEmail(payload);
      if (isErrorResponse(response)) {
        enqueueSnackbar(getResponseMessage(response), { variant: "error" });
        return;
      }

      const result = readResponseData(response, {});
      const emailId = getValue(result, "emailId", "EmailId") || payload.id;
      enqueueSnackbar(getResponseMessage(response, "Το μήνυμα αποθηκεύτηκε"), {
        variant: "success",
      });

      if (action === EMAIL_ACTIONS.SEND) {
        setMail((prev) =>
          prev
            ? {
                ...prev,
                statusId: EMAIL_MESSAGE_STATUSES.PENDING,
              }
            : prev,
        );
      }

      if (action === EMAIL_ACTIONS.DELETE) {
        navigate("/govnr/mail-tool");
        return;
      }

      if (isNew && emailId) {
        navigate(`/govnr/mail-tool/${emailId}`, { replace: true });
        return;
      }

      loadEmail(false);
    } catch (error) {
      enqueueSnackbar(
        getResponseMessage(error, "Δεν ήταν δυνατή η αποθήκευση του μηνύματος"),
        {
          variant: "error",
        },
      );
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const submitAction = (action) => {
    handleSubmit((values) => runAction(action, values))();
  };

  const openTestEmailDialog = () => {
    handleSubmit((values) => {
      setTestEmailMessage({
        subject: values.subject.trim(),
        body: values.body,
      });
    })();
  };

  const confirmConfig = getConfirmConfig(confirmAction);
  const actionBusy = Boolean(actionLoading);
  const refreshEmailQuietly = useCallback(() => loadEmail(false), [loadEmail]);
  const hasActionButtons =
    isEditableStatus(statusId, isNew) || canSend || canStop || canDelete;
  const actionButtons = (
    <>
      {isEditableStatus(statusId, isNew) && (
        <Button
          variant="contained"
          color="secondary"
          sx={{ color: "#fefefe" }}
          size="small"
          startIcon={<MailOutlineIcon />}
          onClick={openTestEmailDialog}
          disabled={actionBusy}
        >
          Αποστολή Δοκιμαστικού Email
        </Button>
      )}
      {isEditableStatus(statusId, isNew) && (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            actionLoading === EMAIL_ACTIONS.DRAFT ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <SaveIcon />
            )
          }
          onClick={() => submitAction(EMAIL_ACTIONS.DRAFT)}
          disabled={actionBusy || !backendAllowsAction(EMAIL_ACTIONS.DRAFT)}
        >
          Αποθήκευση ως πρόχειρο
        </Button>
      )}
      {canSend && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={
            actionLoading === EMAIL_ACTIONS.SEND ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <SendIcon />
            )
          }
          onClick={() => submitAction(EMAIL_ACTIONS.SEND)}
          disabled={actionBusy || !backendAllowsAction(EMAIL_ACTIONS.SEND)}
        >
          Αποστολή
        </Button>
      )}
      {canStop && (
        <Button
          variant="contained"
          color="warning"
          size="small"
          startIcon={<StopCircleIcon />}
          onClick={() => setConfirmAction(EMAIL_ACTIONS.STOP)}
          disabled={actionBusy || !backendAllowsAction(EMAIL_ACTIONS.STOP)}
          sx={MAIL_TOOL_STOP_WARNING_BUTTON_SX}
        >
          Διακοπή
        </Button>
      )}
      {canDelete && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setConfirmAction(EMAIL_ACTIONS.DELETE)}
          disabled={actionBusy || !backendAllowsAction(EMAIL_ACTIONS.DELETE)}
        >
          Διαγραφή
        </Button>
      )}
    </>
  );

  if (detailLoading) {
    return (
      <Box sx={{ p: 2, maxWidth: 1500, mx: "auto" }}>
        <Skeleton height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={520} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1500, mx: "auto", minHeight: "70vh" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title="Πίσω">
            <IconButton onClick={() => navigate("/govnr/mail-tool")}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <MailOutlineIcon color="primary" />
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h4" fontWeight={700}>
                {isNew ? "Νέο μήνυμα" : "Μήνυμα"}
              </Typography>
              {!isNew && <MailStatusChip statusId={statusId} />}
            </Stack>
            {!isNew && (
              <Typography variant="body2" color="text.secondary">
                Δημιουργήθηκε {formatDateTime(mail?.createdAt)}
              </Typography>
            )}
          </Box>
        </Stack>

        {!isNew && (
          <Tooltip title="Ανανέωση">
            <span>
              <IconButton onClick={() => loadEmail()} disabled={actionBusy}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {actionBusy && <LinearProgress sx={{ mb: 2 }} />}

      {showDeliveryLog && (
        <MailDeliveryLog
          emailId={mail?.id || id}
          poll={pollDeliveryLog}
          onDone={refreshEmailQuietly}
        />
      )}

      <Accordion
        disableGutters
        elevation={0}
        expanded={filtersExpanded}
        onChange={(event, expanded) => setFiltersExpanded(expanded)}
        sx={{
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 54,
            px: 2,
            "& .MuiAccordionSummary-content": {
              minWidth: 0,
              my: 1,
            },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ width: "100%", minWidth: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterAltIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Φίλτρα παραληπτών
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {recipientFilterSummary.slice(0, 4).map((summary) => (
                <Chip
                  key={summary}
                  size="small"
                  label={summary}
                  variant="outlined"
                  sx={{
                    maxWidth: 220,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              ))}
              {recipientFilterSummary.length > 4 && (
                <Chip
                  size="small"
                  label={`+${recipientFilterSummary.length - 4}`}
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          <RecipientFilters
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(DEFAULT_RECIPIENT_FILTERS)}
            onSearch={handleFindRecipients}
            seasons={seasons}
            applicationTypes={applicationTypes}
            genders={genders}
            applicationStatuses={applicationStatuses}
            loading={recipientLoading}
            disabled={readOnly || lookupLoading || actionBusy}
            hideTitle
          />
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={9}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <CssTextField
                    {...field}
                    fullWidth
                    label="Θέμα"
                    disabled={readOnly || actionBusy}
                    error={Boolean(errors.subject)}
                    helperText={errors.subject?.message}
                  />
                )}
              />

              <Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Κείμενο
                  </Typography>
                  <Tabs
                    value={bodyTab}
                    onChange={(event, value) => setBodyTab(value)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ minHeight: 40 }}
                  >
                    <Tab
                      value="visual"
                      icon={<EditNoteIcon />}
                      iconPosition="start"
                      label="Επεξεργασία"
                      sx={{ minHeight: 40 }}
                    />
                    <Tab
                      value="preview"
                      icon={<PreviewIcon />}
                      iconPosition="start"
                      label="Προεπισκόπηση"
                      sx={{ minHeight: 40 }}
                    />
                  </Tabs>
                </Stack>

                {bodyTab === "visual" ? (
                  <Controller
                    name="body"
                    control={control}
                    render={({ field }) => (
                      <MailBodyEditor
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={readOnly || actionBusy}
                        error={Boolean(errors.body)}
                        helperText={errors.body?.message}
                      />
                    )}
                  />
                ) : (
                  <Box
                    component="iframe"
                    title="Προεπισκόπηση μηνύματος"
                    sandbox=""
                    srcDoc={bodyValue || "<body></body>"}
                    sx={{
                      width: "100%",
                      height: 466,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                    }}
                  />
                )}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Stack spacing={2}>
            <Paper sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <PeopleAltIcon color="primary" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Παραλήπτες
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedRecipientIds.length} επιλεγμένοι από{" "}
                    {recipients.length}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${selectedRecipientIds.length} επιλεγμένοι`}
                  color="primary"
                />
                <Chip
                  size="small"
                  label={`${recipients.length - selectedRecipientIds.length} εξαιρέθηκαν`}
                  variant="outlined"
                />
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<PeopleAltIcon />}
                onClick={() => {
                  setDialogRecipients(recipients);
                  setRecipientsOpen(true);
                }}
                disabled={recipients.length === 0}
                sx={{ mt: 1.5 }}
              >
                {readOnly ? "Προβολή παραληπτών" : "Επεξεργασία παραληπτών"}
              </Button>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {hasActionButtons && (
        <Paper sx={{ px: 2, py: 1.25, mt: 1.5, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="flex-end"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            {actionButtons}
          </Stack>
        </Paper>
      )}

      <RecipientsDialog
        open={recipientsOpen}
        recipients={dialogRecipients}
        readOnly={readOnly || actionBusy}
        showStatusColumn={showRecipientStatusColumn}
        onClose={() => {
          setDialogRecipients(recipients);
          setRecipientsOpen(false);
        }}
        onApply={(nextRecipients) => {
          setRecipients(nextRecipients);
          setDialogRecipients(nextRecipients);
          setRecipientsOpen(false);
        }}
      />

      <TestEmailDialog
        open={Boolean(testEmailMessage)}
        message={testEmailMessage}
        onClose={() => setTestEmailMessage(null)}
      />

      <ActionConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmConfig.title}
        description={confirmConfig.description}
        confirmLabel={confirmConfig.confirmLabel}
        confirmColor={confirmConfig.confirmColor}
        confirmSx={confirmConfig.confirmSx}
        loading={Boolean(actionLoading)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => runAction(confirmAction, getValues())}
      />
    </Box>
  );
};

export default MailToolEditor;
